// Tipos e utilitários das telas da Folha. Dinheiro chega em centavos (texto) e sai em centavos; nunca float.
// Percentual em pontos-base (750 = 7,5%).
export type EstadoCompetencia = 'aberta' | 'calculada' | 'conferida' | 'fechando' | 'fechada';
export type Natureza = 'provento' | 'desconto' | 'encargo';

export interface Competencia {
  id: string;
  periodo: string;
  estado: EstadoCompetencia;
  versao: number;
  moeda: string;
  funcionarios: number;
  /** SM2-R6-01: totais só para o gestor da folha (folha.editar); null para os demais. */
  proventos_centavos: string | null;
  descontos_centavos: string | null;
  liquido_centavos: string | null;
  encargos_centavos: string | null;
  custo_centavos: string | null;
  calculada_em: string | null;
  conferida_em: string | null;
  fechada_em: string | null;
  fechamento_job_id: string | null;
  fechamento_erro: string | null;
  motivo_reabertura: string | null;
  /** M12.4: totais só dos holerites visíveis (empresas permitidas ou filtro por empresa). */
  parcial: boolean;
  data_pagamento: string;
}

export interface FichaCompetencia extends Competencia {
  gestor: boolean;
  /** M12.4: quem vê tem empresas permitidas (a competência é do grupo: comandos ficam com quem acessa todas). */
  restrito: boolean;
  holerites: { id: string; funcionario_id: string; nome: string; matricula: string; funcao: string | null; departamento: string | null; empresa_id: string | null; empresa: string | null; proventos_centavos: string; descontos_centavos: string; liquido_centavos: string; custo_centavos: string; tem_email: boolean; enviado_em: string | null }[];
  variaveis: { id: string; funcionario_id: string; funcionario: string | null; evento_id: string; evento_codigo: string; evento: string; natureza: Natureza; valor_centavos: string; observacao: string | null }[];
  lancamentos: { id: string; versao: number; centro: string | null; valor_centavos: string; moeda: string; lancamento_id: string; numero: string; estornado_em: string | null }[];
  trilha: { id: string; versao: number; acao: string; job_id: string | null; detalhes: Record<string, unknown>; created_at: string; usuario: string | null }[];
}

export interface Evento {
  id: string;
  codigo: string;
  nome: string;
  natureza: Natureza;
  tipo: 'fixo' | 'variavel';
  calculo: 'valor' | 'percentual' | 'tabela';
  valor_centavos: string | null;
  percentual_bp: number | null;
  tabela_codigo: string | null;
  base: 'salario' | 'proventos' | 'proventos_menos_descontos' | null;
  ordem: number;
  centro_custo_id: string | null;
  centro: string | null;
  moeda: string;
}

export interface Tabela {
  id: string;
  codigo: string;
  nome: string;
  vigencia: string;
  modo: 'progressiva' | 'faixa';
  demonstracao: boolean;
  faixas: { ate_centavos: string | null; aliquota_bp: number; deducao_centavos: string }[];
}

export interface Beneficio {
  id: string;
  nome: string;
  alvo: 'funcionario' | 'funcao';
  funcionario_id: string | null;
  funcionario: string | null;
  funcao_id: string | null;
  funcao: string | null;
  modo: 'valor' | 'percentual';
  funcionario_centavos: string;
  empresa_centavos: string;
  funcionario_bp: number;
  empresa_bp: number;
  ativo: boolean;
  moeda: string;
  alcance: number;
}

export interface HoleriteResumo {
  id: string;
  competencia_id: string;
  periodo: string;
  estado_competencia: EstadoCompetencia;
  funcionario_id: string;
  nome: string;
  matricula: string;
  funcao: string | null;
  departamento: string | null;
  /** M12.4: empresa contratante (null = do grupo). */
  empresa_id: string | null;
  empresa: string | null;
  moeda: string;
  proventos_centavos: string;
  descontos_centavos: string;
  liquido_centavos: string;
  enviado_em: string | null;
}

export interface HoleriteCompleto extends HoleriteResumo {
  salario_centavos: string;
  encargos_centavos: string;
  custo_centavos: string;
  data_pagamento: string;
  linhas: { codigo: string; nome: string; natureza: Natureza; referencia: string | null; valor_centavos: string }[];
}

export interface Opcoes {
  /** SEGA-R32-01: as listas só chegam para gestores ou quem tem folha.editar/folha.admin. */
  funcionarios?: { id: string; nome: string; matricula: string; funcao: string | null; departamento: string | null }[];
  funcoes?: { id: string; nome: string }[];
  departamentos?: { id: string; nome: string; centro_custo_id: string | null }[];
  contas?: { id: string; nome: string; moeda: string }[];
  centros?: { id: string; codigo: string; nome: string }[];
  categorias?: { id: string; nome: string }[];
  config: { conta_id: string | null; categoria_id: string | null; centro_padrao_id: string | null; dia_pagamento: number };
}

export const SELECT = 'h-10 min-w-0 rounded-lg border border-borda bg-sup-1 px-2 text-sm text-tinta-1 outline-none focus:border-acento';

export function formatarDinheiro(centavos: string | null | undefined, moeda = 'BRL', idioma = 'pt-BR'): string {
  if (centavos === null || centavos === undefined || centavos === '') return '';
  const c = BigInt(centavos);
  const neg = c < 0n;
  const a = neg ? -c : c;
  const decimal = `${neg ? '-' : ''}${(a / 100n).toString()}.${(a % 100n).toString().padStart(2, '0')}`;
  return new Intl.NumberFormat(idioma, { style: 'currency', currency: moeda }).format(decimal as unknown as number);
}

/** Texto em reais ('1.234,56') para centavos inteiros em texto, sem float. 'invalido' se não reconhece. */
export function reaisParaCentavos(texto: string): string | null {
  const t = texto.trim().replace(/\s|R\$/g, '');
  if (!t) return null;
  const m = /^(\d{1,3}(?:\.\d{3})*|\d+)(?:,(\d{1,2}))?$/.exec(t);
  if (!m) return 'invalido';
  return (BigInt(m[1]!.replace(/\./g, '')) * 100n + BigInt((m[2] ?? '').padEnd(2, '0') || '0')).toString();
}

/** Centavos para o campo de digitação ('154990' vira '1549,90'). */
export function centavosParaCampo(c: string | null | undefined): string {
  if (!c) return '';
  const v = BigInt(c);
  return `${(v / 100n).toString()},${(v % 100n).toString().padStart(2, '0')}`;
}

/** Percentual digitado ('7,5' ou '12') para pontos-base inteiros (750, 1200), sem float. */
export function percentualParaBp(texto: string): number | null | 'invalido' {
  const t = texto.trim().replace('%', '');
  if (!t) return null;
  const m = /^(\d{1,3})(?:[,.](\d{1,2}))?$/.exec(t);
  if (!m) return 'invalido';
  const bp = Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0') || '0');
  return bp > 10_000 ? 'invalido' : bp;
}

/** 750 vira '7,5%' (idioma da tela). */
export function bpTexto(bp: number, idioma = 'pt-BR'): string {
  return `${new Intl.NumberFormat(idioma, { maximumFractionDigits: 2 }).format((bp / 100) as number)}%`;
}

export const bpParaCampo = (bp: number): string => (bp % 100 === 0 ? String(bp / 100) : `${Math.trunc(bp / 100)},${String(bp % 100).padStart(2, '0').replace(/0$/, '')}`);

/** '2026-09' vira 'setembro de 2026' no idioma da tela. */
export function periodoTexto(p: string, idioma: string, curto = false): string {
  const d = new Date(`${p}-01T12:00:00Z`);
  const s = new Intl.DateTimeFormat(idioma, { month: curto ? 'short' : 'long', year: 'numeric', timeZone: 'UTC' }).format(d);
  return s.charAt(0).toLocaleUpperCase(idioma) + s.slice(1);
}

export const formatarData = (iso: string | null | undefined, idioma: string) => (iso ? new Intl.DateTimeFormat(idioma, { timeZone: 'UTC' }).format(new Date(`${iso.slice(0, 10)}T00:00:00Z`)) : '');

export const formatarDataHora = (iso: string | null | undefined, idioma: string) =>
  iso ? new Intl.DateTimeFormat(idioma, { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso)) : '';

export function periodoAtual(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date()).slice(0, 7);
}

export const COR_ESTADO: Record<EstadoCompetencia, string> = { aberta: 'azul', calculada: 'ciano', conferida: 'roxo', fechando: 'laranja', fechada: 'sucesso' };
export const COR_NATUREZA: Record<Natureza, string> = { provento: 'sucesso', desconto: 'laranja', encargo: 'roxo' };
